// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { AttentionThresholdSetting } from '@/account/api/types';
import {
  type AttentionThresholdFormFields,
  type AttentionThresholdRowsByKey,
  THRESHOLD_TIER_FIELDS,
  type ThresholdFormRowData,
  type ThresholdTierField,
  getAttentionThresholdRow,
} from '@/account/components/attention-threshold-form';
import {
  getAttentionThresholdEntityLabel,
  getAttentionThresholdReasonCopy,
  getAttentionThresholdSettingKey,
} from '@/account/constants/attention-threshold-labels';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { Restore } from '@/constants/hardfin-standard-icons';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { NumberFormatValues } from 'react-number-format';

const TIER_COLUMNS: {
  field: ThresholdTierField;
  label: string;
  color: 'info' | 'warning' | 'error';
}[] = [
  { field: 'noticeDays', label: 'Notice', color: 'info' },
  { field: 'warningDays', label: 'Warning', color: 'warning' },
  { field: 'criticalDays', label: 'Critical', color: 'error' },
];

function isBlankThresholdValue(value: number | null): boolean {
  return value === null;
}

function rowHasAnyThresholdValue(row: ThresholdFormRowData): boolean {
  return THRESHOLD_TIER_FIELDS.some(
    (field) => !isBlankThresholdValue(row[field])
  );
}

function thresholdFieldPath<
  Values extends FieldValues & AttentionThresholdFormFields,
>(settingKey: string, field: ThresholdTierField): FieldPath<Values> {
  return `thresholds.${settingKey}.${field}` as FieldPath<Values>;
}

function numberFormatValueToThresholdValue(
  values: NumberFormatValues
): number | null {
  return values.value === '' ? null : (values.floatValue ?? null);
}

function thresholdRowWithChangedValue(
  row: ThresholdFormRowData | undefined,
  field: ThresholdTierField,
  value: number | null
): ThresholdFormRowData {
  return {
    noticeDays: row?.noticeDays ?? null,
    warningDays: row?.warningDays ?? null,
    criticalDays: row?.criticalDays ?? null,
    [field]: value,
  };
}

function isThresholdFieldValidForCurrentRow(
  row: ThresholdFormRowData,
  field: ThresholdTierField
): boolean {
  switch (field) {
    case 'noticeDays':
      return row.noticeDays !== null;
    case 'warningDays':
      return (
        row.warningDays !== null &&
        (row.noticeDays === null || row.noticeDays <= row.warningDays)
      );
    case 'criticalDays':
      return (
        row.criticalDays !== null &&
        (row.warningDays === null || row.warningDays <= row.criticalDays)
      );
  }
}

function getThresholdRowErrors(
  errors: unknown,
  settingKey: string
): Partial<Record<ThresholdTierField, unknown>> | undefined {
  if (errors === null || typeof errors !== 'object') {
    return undefined;
  }

  const rowErrors = (errors as Record<string, unknown>)[settingKey];
  if (rowErrors === null || typeof rowErrors !== 'object') {
    return undefined;
  }

  return rowErrors as Partial<Record<ThresholdTierField, unknown>>;
}

interface AttentionThresholdTableProps<
  Values extends FieldValues & AttentionThresholdFormFields,
> {
  clearErrors: UseFormClearErrors<Values>;
  control: Control<Values>;
  errors: unknown;
  setValue: UseFormSetValue<Values>;
  settings: AttentionThresholdSetting[];
  disabled: boolean;
}

export function AttentionThresholdTable<
  Values extends FieldValues & AttentionThresholdFormFields,
>(props: AttentionThresholdTableProps<Values>) {
  const rows = useWatch({
    control: props.control,
    name: 'thresholds' as FieldPath<Values>,
  }) as AttentionThresholdRowsByKey | undefined;

  const handleResetRow = (settingKey: string) => {
    THRESHOLD_TIER_FIELDS.forEach((field) => {
      const path = thresholdFieldPath<Values>(settingKey, field);
      props.setValue(path, null as FieldPathValue<Values, typeof path>, {
        shouldDirty: true,
      });
    });
    props.clearErrors(
      THRESHOLD_TIER_FIELDS.map((field) =>
        thresholdFieldPath<Values>(settingKey, field)
      )
    );
  };

  const handleThresholdValueChange = (
    settingKey: string,
    field: ThresholdTierField,
    row: ThresholdFormRowData | undefined,
    values: NumberFormatValues
  ) => {
    const nextRow = thresholdRowWithChangedValue(
      row,
      field,
      numberFormatValueToThresholdValue(values)
    );
    const rowErrors = getThresholdRowErrors(props.errors, settingKey);
    THRESHOLD_TIER_FIELDS.forEach((candidateField) => {
      if (
        rowErrors?.[candidateField] &&
        isThresholdFieldValidForCurrentRow(nextRow, candidateField)
      ) {
        props.clearErrors(
          thresholdFieldPath<Values>(settingKey, candidateField)
        );
      }
    });
  };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ overflowX: 'auto' }}
    >
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>Entity</TableCell>
            <TableCell>Reason</TableCell>
            {TIER_COLUMNS.map((column) => (
              <TableCell key={column.field} align="right">
                <Chip
                  label={column.label}
                  color={column.color}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {props.settings.map((setting) => {
            const settingKey = getAttentionThresholdSettingKey(setting);
            const copy = getAttentionThresholdReasonCopy(setting);
            const entityLabel = getAttentionThresholdEntityLabel(
              setting.entity
            );
            const row = getAttentionThresholdRow(rows, settingKey);
            const hasThresholdValue =
              row !== undefined && rowHasAnyThresholdValue(row);

            return (
              <TableRow key={settingKey}>
                <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                  <Typography variant="body2">{entityLabel}</Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                  <Typography variant="body2">{copy.reasonLabel}</Typography>
                </TableCell>
                {TIER_COLUMNS.map((column) => (
                  <TableCell
                    key={column.field}
                    align="right"
                    sx={{ verticalAlign: 'top' }}
                  >
                    <Box sx={{ width: 120, marginLeft: 'auto' }}>
                      <FormNumberTextField<Values>
                        allowNegative={false}
                        control={props.control}
                        disabled={props.disabled}
                        fullWidth
                        helperText={`Default: ${setting.default[column.field]}`}
                        maximumFractionDigits={0}
                        name={thresholdFieldPath<Values>(
                          settingKey,
                          column.field
                        )}
                        nullable
                        numeric
                        onChange={(values) => {
                          handleThresholdValueChange(
                            settingKey,
                            column.field,
                            row,
                            values
                          );
                        }}
                        InputProps={{
                          inputProps: {
                            'aria-label': `${entityLabel} ${copy.reasonLabel.toLowerCase()} ${column.label.toLowerCase()} days`,
                            style: { textAlign: 'right' },
                          },
                        }}
                      />
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{ verticalAlign: 'top', paddingTop: 1.5 }}
                >
                  <Tooltip title="Reset to defaults" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={props.disabled || !hasThresholdValue}
                        onClick={() => handleResetRow(settingKey)}
                        aria-label={`Reset ${entityLabel.toLowerCase()} ${copy.reasonLabel.toLowerCase()} thresholds to defaults`}
                      >
                        <Restore fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
